import { makeStyles } from '@mui/styles';
import { Theme } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';
import { CheckCircleOutline, CancelOutlined } from '@mui/icons-material';

import { PAGE_TITLE } from '../../../consts';
import { Icon } from '../../../../../common/icon';
import { getDocApiGuidePath, getDocApiDocumentationPath } from '../../../../../../api/utils';

import DocLink from './docLink';
import { ProductCard as Props } from '../interfaces';

interface StyleProps {
  isActive: boolean;
}

const useStyles = makeStyles((theme: Theme) => ({
  productCardContainer: {
    padding: theme.spacing(3),
    background: theme.palette.background.paper,
    borderRadius: theme.customComponents.card.borderRadius,
    boxShadow: theme.customComponents.card.boxShadow,
    boxSizing: 'border-box',
    border: ({ isActive }: StyleProps) => (isActive ? '2px solid green' : 'none'),
    display: 'flex',
    flexDirection: 'column'
  },
  icon: {
    color: 'inherit'
  },
  docIcon: {
    fontSize: '18px'
  },
  docContainer: {
    color: theme.palette.primary.main,
    display: 'flex',
    marginTop: theme.spacing(2)
  },
  doc: {
    color: 'inherit',
    marginLeft: '2px'
  },
  cardTitle: {
    display: 'flex',
    alignItems: 'center',
    color: ({ isActive }: StyleProps) => (isActive ? theme.palette.primary.main : theme.palette.common.black)
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2)
  },
  stateLabel: {
    color: ({ isActive }: StyleProps) => (isActive ? theme.palette.primary.main : theme.customPalette.silverSand),
    display: 'flex',
    alignItems: 'center'
  },
  stateLabelText: {
    color: 'inherit',
    marginTop: '5px',
    marginLeft: '4px'
  }
}));

const ProductCard = ({ title, isActive, icon, description, api }: Props) => {
  const classes = useStyles({ isActive });

  return (
    <Box className={classes.productCardContainer} data-testid={`product-card-${isActive ? 'enabled' : 'disabled'}`}>
      <Box className={classes.cardHeader}>
        <Box className={classes.cardTitle}>
          <Box mr={1}>
            <Icon className={classes.icon} icon={icon} />
          </Box>
          <Typography color="inherit" variant="h3">
            {title}
          </Typography>
        </Box>
        <Box className={classes.stateLabel}>
          <Icon icon={isActive ? CheckCircleOutline : CancelOutlined} />
          <Typography className={classes.stateLabelText} variant="h4">
            {isActive ? 'Enabled' : 'Disabled'}
          </Typography>
        </Box>
      </Box>
      <Box flex="1 1 auto" justifyContent="space-between" display="flex" flexDirection="column">
        <Box>{description}</Box>
        <Box mt={2}>
          {api.guide !== null ? (
            <Box mb={1}>
              <DocLink text="API guide" href={getDocApiGuidePath(api.guide, api.guideSufix)} />
            </Box>
          ) : null}
          {api.doc !== null ? (
            <DocLink
              text="API documentation"
              href={getDocApiDocumentationPath(api.doc, title === PAGE_TITLE.USSD_SESSION)}
            />
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};

export default ProductCard;
